import { fetchWithAuth } from "@/lib/api-helpers";
import { toast } from "@/lib/toast";
import { fileTooLargeMessage } from "@/lib/upload-limits";

// ── Download helper ───────────────────────────────────────────────────────────

async function downloadBlob(url: string, filename: string, workspaceId: string): Promise<boolean> {
  try {
    const response = await fetchWithAuth(url, {
      headers: { "x-workspace-id": workspaceId },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      const msg = (() => {
        try { return JSON.parse(text).message; } catch { return text || "Export failed"; }
      })();
      toast.error(msg);
      return false;
    }

    // Use Content-Type to fix extension (Content-Type is always readable, unlike Content-Disposition which CORS can block)
    const contentType = response.headers.get("content-type") || "";
    let resolvedFilename = filename;
    if (contentType.includes("application/zip") && !filename.endsWith(".zip")) {
      resolvedFilename = filename.replace(/\.[^.]+$/, ".zip");
    } else if (contentType.includes("application/pdf") && !filename.endsWith(".pdf")) {
      resolvedFilename = filename.replace(/\.[^.]+$/, ".pdf");
    }
    // Also try Content-Disposition if CORS exposes it
    const disposition = response.headers.get("content-disposition") || "";
    const cdMatch = disposition.match(/filename="([^"]+)"/);
    if (cdMatch?.[1]) resolvedFilename = cdMatch[1];

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = resolvedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (err) {
    toast.error("Network error during export");
    return false;
  }
}

// ── Export with options ───────────────────────────────────────────────────────

export type ExportFormat = "markdown_csv" | "html" | "pdf";

export interface ExportOptions {
  format: ExportFormat;
  pageContent: "everything" | "exclude_files_images";
  includeSubpages: boolean;
  createFolders: boolean;
  pageFormat: string;
  scalePercent: number;
}

export async function exportWithOptions(
  pageId: string,
  pageTitle: string,
  workspaceId: string,
  opts: ExportOptions,
  blockType: "page" | "collection_view" = "page",
) {
  const params = new URLSearchParams({
    pageContent: opts.pageContent,
    includeSubpages: String(opts.includeSubpages),
    createFolders: String(opts.createFolders),
  });

  if (opts.format === "pdf") {
    params.set("pageFormat", opts.pageFormat);
    params.set("scalePercent", String(opts.scalePercent));
    const filename = `${sanitize(pageTitle)}.pdf`;
    const ok = await downloadBlob(`/api/export/page/${pageId}/pdf?${params}`, filename, workspaceId);
    if (ok) toast.success(`Exported "${pageTitle}" as PDF`);
    return;
  }

  if (blockType === "collection_view") {
    // Database: always CSV
    const filename = `${sanitize(pageTitle)}.csv`;
    const ok = await downloadBlob(`/api/export/database/${pageId}/csv?${params}`, filename, workspaceId);
    if (ok) toast.success(`Exported "${pageTitle}" as CSV`);
    return;
  }

  if (opts.format === "html") {
    if (opts.includeSubpages) {
      const filename = `${sanitize(pageTitle)}.zip`;
      const ok = await downloadBlob(`/api/export/page/${pageId}/zip?${params}&format=html`, filename, workspaceId);
      if (ok) toast.success(`Exported "${pageTitle}" as HTML ZIP`);
    } else {
      const filename = `${sanitize(pageTitle)}.html`;
      const ok = await downloadBlob(`/api/export/page/${pageId}/html?${params}`, filename, workspaceId);
      if (ok) toast.success(`Exported "${pageTitle}" as HTML`);
    }
    return;
  }

  // markdown_csv: always ZIP
  const filename = `${sanitize(pageTitle)}.zip`;
  const ok = await downloadBlob(`/api/export/page/${pageId}/zip?${params}`, filename, workspaceId);
  if (ok) toast.success(`Exported "${pageTitle}"`);
}

// ── Export functions ──────────────────────────────────────────────────────────

export async function exportPageAsMarkdown(pageId: string, pageTitle: string, workspaceId: string) {
  const filename = `${sanitize(pageTitle)}.md`;
  const ok = await downloadBlob(`/api/export/page/${pageId}/markdown`, filename, workspaceId);
  if (ok) toast.success(`Exported "${pageTitle}" as Markdown`);
}

export async function exportPageAsHtml(pageId: string, pageTitle: string, workspaceId: string) {
  const filename = `${sanitize(pageTitle)}.html`;
  const ok = await downloadBlob(`/api/export/page/${pageId}/html`, filename, workspaceId);
  if (ok) toast.success(`Exported "${pageTitle}" as HTML`);
}

export async function exportPageAsZip(pageId: string, pageTitle: string, workspaceId: string) {
  const filename = `${sanitize(pageTitle)}.zip`;
  const ok = await downloadBlob(`/api/export/page/${pageId}/zip`, filename, workspaceId);
  if (ok) toast.success(`Exported "${pageTitle}" as ZIP`);
}

export async function exportDatabaseAsCsv(databaseId: string, dbTitle: string, workspaceId: string) {
  const filename = `${sanitize(dbTitle)}.csv`;
  const ok = await downloadBlob(`/api/export/database/${databaseId}/csv`, filename, workspaceId);
  if (ok) toast.success(`Exported "${dbTitle}" as CSV`);
}

export async function exportWorkspaceAsZip(workspaceId: string) {
  const ok = await downloadBlob("/api/export/workspace/zip", "workspace-export.zip", workspaceId);
  if (ok) toast.success("Workspace export downloaded");
}

// ── Import functions ──────────────────────────────────────────────────────────

export interface ImportResult {
  success: boolean;
  pageId?: string;
  databaseId?: string;
  title: string;
  blockCount?: number;
  rowCount?: number;
  pageCount?: number;
}

// Unified import — auto-detects format from file extension
export async function importFile(
  file: File,
  parentId: string,
  parentTable: string,
  workspaceId: string,
  options?: { databaseTitle?: string; workareaId?: string; columnTypes?: Record<number, string> },
): Promise<ImportResult | null> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  const endpointMap: Record<string, string> = {
    md: "/api/import/markdown",
    txt: "/api/import/txt",
    html: "/api/import/html",
    htm: "/api/import/html",
    csv: "/api/import/csv",
    zip: "/api/import/zip",
    pdf: "/api/import/pdf",
    docx: "/api/import/docx",
    enex: "/api/import/enex",
  };
  const endpoint = endpointMap[ext];
  if (!endpoint) {
    toast.error(`Unsupported file type: .${ext}`);
    return null;
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("parentId", parentId);
  formData.append("parentTable", parentTable);
  formData.append("workspaceId", workspaceId);
  if (options?.databaseTitle) formData.append("databaseTitle", options.databaseTitle);
  if (options?.workareaId) formData.append("workareaId", options.workareaId);
  if (options?.columnTypes && Object.keys(options.columnTypes).length > 0) {
    formData.append("columnTypes", JSON.stringify(options.columnTypes));
  }

  try {
    const response = await fetchWithAuth(endpoint, {
      method: "POST",
      headers: { "x-workspace-id": workspaceId },
      body: formData,
    });
    if (response.status === 413) {
      toast.error(fileTooLargeMessage());
      return null;
    }
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(data.message || "Import failed");
      return null;
    }
    return response.json() as Promise<ImportResult>;
  } catch {
    toast.error("Network error during import");
    return null;
  }
}

// ── App imports (Trello boards / Asana projects → databases) ─────────────────

export async function listTrelloBoards(
  workspaceId: string,
): Promise<{ connected: boolean; boards: Array<{ shortLink: string; name: string }> }> {
  try {
    const response = await fetchWithAuth(
      `/api/trello/boards?workspaceId=${encodeURIComponent(workspaceId)}`,
      { headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return { connected: false, boards: [] };
    return response.json();
  } catch {
    return { connected: false, boards: [] };
  }
}

export async function listAsanaProjects(
  workspaceId: string,
): Promise<{ connected: boolean; projects: Array<{ gid: string; name: string; workspaceName: string }> }> {
  try {
    const response = await fetchWithAuth(
      `/api/asana/projects?workspaceId=${encodeURIComponent(workspaceId)}`,
      { headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return { connected: false, projects: [] };
    return response.json();
  } catch {
    return { connected: false, projects: [] };
  }
}

export async function listConfluenceSpaces(
  workspaceId: string,
): Promise<{ connected: boolean; spaces: Array<{ cloudId: string; siteName: string; spaceId: string; key: string; name: string }> }> {
  try {
    const response = await fetchWithAuth(
      `/api/confluence/spaces?workspaceId=${encodeURIComponent(workspaceId)}`,
      { headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return { connected: false, spaces: [] };
    return response.json();
  } catch {
    return { connected: false, spaces: [] };
  }
}

/**
 * Synced databases — distinct from the importers above.
 *
 * An import makes a detached copy you then edit. A sync makes a mirror
 * that keeps updating, so the two live in the same modal but must never
 * be described interchangeably to the user.
 */
export type SyncProvider = "jira" | "gitlab" | "asana";

export interface SyncProjectOption {
  /** Pass back verbatim — the format differs per provider and is easy to get wrong. */
  remoteKey: string;
  name: string;
  subtitle?: string;
}

/**
 * Projects available to sync. `truncated` means the provider had more than
 * we listed; the caller must say so rather than presenting a prefix as the
 * whole list.
 */
export async function listSyncProjects(
  provider: SyncProvider,
  workspaceId: string,
): Promise<{ connected: boolean; items: SyncProjectOption[]; truncated: boolean }> {
  const empty = { connected: false, items: [], truncated: false };
  try {
    const response = await fetchWithAuth(
      `/api/${provider}/projects?workspaceId=${encodeURIComponent(workspaceId)}`,
      { headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return empty;
    const data = await response.json();
    if (!data?.connected) return { ...empty, connected: false };

    // The three providers return different shapes; this is the union of
    // the fields each one supplies, so the mapping below stays checked.
    type RawProject = {
      remoteKey?: string;
      gid?: string;
      name?: string;
      key?: string;
      siteName?: string;
      workspaceName?: string;
      pathWithNamespace?: string;
    };

    const items: SyncProjectOption[] = (data.projects ?? [])
      .map((p: RawProject): SyncProjectOption | null => {
        const name = p.name ?? "";
        if (provider === "asana") {
          return p.gid ? { remoteKey: p.gid, name, subtitle: p.workspaceName } : null;
        }
        if (!p.remoteKey) return null;
        return provider === "jira"
          ? { remoteKey: p.remoteKey, name, subtitle: `${p.key ?? ""} · ${p.siteName ?? ""}` }
          : { remoteKey: p.remoteKey, name, subtitle: p.pathWithNamespace };
      })
      // A row without a usable key would render a button that silently
      // fails on click, so drop it rather than offer it.
      .filter((p: SyncProjectOption | null): p is SyncProjectOption => p !== null);
    return { connected: true, items, truncated: Boolean(data.truncated) };
  } catch {
    return empty;
  }
}

export interface CreateSyncResult {
  syncId: string;
  pageId: string;
  dataSourceId: string;
  remoteName: string;
  run?: {
    created: number;
    updated: number;
    truncated: boolean;
    status: "ok" | "partial" | "error";
    message?: string;
  };
}

export async function createSyncedDatabase(args: {
  connectorId: SyncProvider;
  remoteKey: string;
  parentId: string;
  parentTable: string;
  workspaceId: string;
  workareaId?: string;
}): Promise<CreateSyncResult | null> {
  const { workspaceId } = args;
  try {
    const response = await fetchWithAuth("/api/synced-databases", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-workspace-id": workspaceId },
      body: JSON.stringify(args),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // The API distinguishes these deliberately; passing the message
      // through means "already synced" doesn't read as a crash.
      toast.error(data.message || "Could not create the synced database");
      return null;
    }
    return (await response.json()) as CreateSyncResult;
  } catch {
    toast.error("Network error while creating the synced database");
    return null;
  }
}

async function postAppImport(
  endpoint: string,
  payload: Record<string, string | undefined>,
  workspaceId: string,
): Promise<ImportResult | null> {
  try {
    const response = await fetchWithAuth(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-workspace-id": workspaceId },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(data.message || "Import failed");
      return null;
    }
    return response.json() as Promise<ImportResult>;
  } catch {
    toast.error("Network error during import");
    return null;
  }
}

export function importTrelloBoard(args: {
  boardShortLink: string;
  parentId: string;
  parentTable: string;
  workspaceId: string;
  workareaId?: string;
}): Promise<ImportResult | null> {
  const { workspaceId, ...rest } = args;
  return postAppImport("/api/import/trello-board", { ...rest, workspaceId }, workspaceId);
}

export function importConfluenceSpace(args: {
  cloudId: string;
  spaceId: string;
  parentId: string;
  parentTable: string;
  workspaceId: string;
  workareaId?: string;
}): Promise<ImportResult | null> {
  const { workspaceId, ...rest } = args;
  return postAppImport("/api/import/confluence-space", { ...rest, workspaceId }, workspaceId);
}

export function importAsanaProject(args: {
  projectGid: string;
  parentId: string;
  parentTable: string;
  workspaceId: string;
  workareaId?: string;
}): Promise<ImportResult | null> {
  const { workspaceId, ...rest } = args;
  return postAppImport("/api/import/asana-project", { ...rest, workspaceId }, workspaceId);
}

export async function importMarkdownFile(
  file: File,
  parentId: string,
  parentTable: string,
  workspaceId: string,
  workareaId?: string,
): Promise<ImportResult | null> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("parentId", parentId);
  formData.append("parentTable", parentTable);
  formData.append("workspaceId", workspaceId);
  if (workareaId) formData.append("workareaId", workareaId);

  try {
    const response = await fetchWithAuth("/api/import/markdown", {
      method: "POST",
      headers: { "x-workspace-id": workspaceId },
      body: formData,
    });

    if (response.status === 413) {
      toast.error(fileTooLargeMessage());
      return null;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(data.message || "Import failed");
      return null;
    }

    return response.json() as Promise<ImportResult>;
  } catch {
    toast.error("Network error during import");
    return null;
  }
}

export async function importCsvFile(
  file: File,
  parentId: string,
  parentTable: string,
  workspaceId: string,
  databaseTitle?: string,
  workareaId?: string,
  columnTypes?: Record<number, string>,
): Promise<ImportResult | null> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("parentId", parentId);
  formData.append("parentTable", parentTable);
  formData.append("workspaceId", workspaceId);
  if (databaseTitle) formData.append("databaseTitle", databaseTitle);
  if (workareaId) formData.append("workareaId", workareaId);
  if (columnTypes && Object.keys(columnTypes).length > 0) {
    formData.append("columnTypes", JSON.stringify(columnTypes));
  }

  try {
    const response = await fetchWithAuth("/api/import/csv", {
      method: "POST",
      headers: { "x-workspace-id": workspaceId },
      body: formData,
    });

    if (response.status === 413) {
      toast.error(fileTooLargeMessage());
      return null;
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(data.message || "CSV import failed");
      return null;
    }

    return response.json() as Promise<ImportResult>;
  } catch {
    toast.error("Network error during import");
    return null;
  }
}

// ── Utils ─────────────────────────────────────────────────────────────────────

function sanitize(name: string): string {
  return (name || "untitled").replace(/[<>:"/\\|?*]/g, "_").slice(0, 100);
}
